import { useCallback, useMemo } from 'react';
import {
	ToothFracturePosition,
	ToothFractureType,
	ToothPathology,
	ToothPathologyFracture,
} from '../../../../../graphql/graphql';
import { find, findIndex, xor } from 'lodash';

interface Props {
	pathology?: ToothPathology;
	setPathology: (pathology: ToothPathology) => void;
}

type FractureSimpleValueKey = keyof Omit<
	ToothPathologyFracture,
	'type' | 'positions' | '__typename'
>;

export default function useToothPathologyFractures({
	pathology,
	setPathology,
}: Props) {
	const buildFractureArray = useCallback(
		(
			currentFractures: ToothPathologyFracture[] | undefined,
			newFracture: ToothPathologyFracture,
		): ToothPathologyFracture[] => {
			const fractures = currentFractures ?? [];
			const index = findIndex(fractures, { type: newFracture.type });

			return index >= 0
				? [
						...fractures.slice(0, index),
						newFracture,
						...fractures.slice(index + 1),
				  ]
				: [...fractures, newFracture];
		},
		[],
	);

	const setPosition = useCallback(
		(type: ToothFractureType, position: ToothFracturePosition) => {
			const existingFracture = pathology?.fractures?.find(
				fracture => fracture.type === type,
			);

			const positions = existingFracture
				? xor(existingFracture.positions, [position])
				: [position];

			const newFracture: ToothPathologyFracture = {
				...existingFracture,
				type,
				positions,
			};

			const newFractures = buildFractureArray(
				pathology?.fractures,
				newFracture,
			);

			setPathology({
				...pathology,
				fractures: newFractures,
			});
		},
		[pathology, setPathology, buildFractureArray],
	);

	const setSimpleValue = useCallback(
		<T extends FractureSimpleValueKey>(
			type: ToothFractureType,
			key: T,
			value: ToothPathologyFracture[T],
		) => {
			const existingFracture = find(pathology?.fractures, { type });

			const newFracture: ToothPathologyFracture = {
				positions: [],
				...existingFracture,
				type,
				[key]: value,
			};

			const newFractures = buildFractureArray(
				pathology?.fractures,
				newFracture,
			);

			setPathology({
				...pathology,
				fractures: newFractures,
			});
		},
		[pathology, setPathology, buildFractureArray],
	);

	return useMemo(
		() => ({
			fractures: {
				values: pathology?.fractures,
				setPosition,
				setSimpleValue,
			},
		}),
		[pathology, setPosition, setSimpleValue],
	);
}
